import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import css from './SimilarCreativesTab.css';
import sharedCss from './sharedTabStyles.css';
import { CreativeType } from '../../types';
import { Dimensions } from '../../consts';
import CreativeChart from './CreativeChart';
import { BAR_CHART, COLUMN_CHART, SCATTER_CHART } from '../../../utils/dashboardConsts';
import { formatMetricValue } from '../creativeCardData/metricDisplayUtils';
import {
    BreakdownType,
    DEFAULT_COL_DEF,
    SIMILAR_CREATIVES_KEYS_PREFIX,
    SIMILAR_CREATIVES_TAB_TITLE,
    SimilarCreativesKeys,
} from './consts';
import { Spinner } from '../../../components/widgets';

const getScatterTooltipFormatter = (xMetric, yMetric) =>
    function scatterTooltipFormatter() {
        const { point } = this;
        if (!point) return '';

        const { name, metricsData = [] } = point;
        const selectedMetricLabels = [xMetric?.display_name, yMetric?.display_name];
        const filteredMetrics = metricsData.filter(m => selectedMetricLabels.includes(m.label));

        return filteredMetrics.reduce(
            (html, { label, value, displayFormat }) =>
                `${html}${label}: ${formatMetricValue(value, displayFormat)}<br/>`,
            `${name}<br/>`
        );
    };

const getBreakdownTooltipFormatter = (creativeCountByCategory, metricLabel, metricDisplayFormat, creativesLabel) =>
    function tooltipFormatter() {
        const { point } = this;
        if (!point) return '';

        const { category } = point;
        const creativeCount = creativeCountByCategory[category] || 0;
        const formattedValue = formatMetricValue(point.y, metricDisplayFormat);

        return `${category}<br/>${metricLabel}: ${formattedValue}<br/>${creativesLabel}: ${creativeCount}`;
    };

const SimilarCreativesTab = ({
    similarCreatives = [],
    isLoadingSimilarCreatives = false,
    similarCreativesError = false,
    chartData = {},
    translate = () => '',
}) => {
    const {
        overview = {},
        specAnalysis = {},
        scatterAnalysis = {},
        tagAnalysis = {},
        aiDimensionOptions = [],
        chartMetrics = [],
        specDimensions = [],
    } = chartData;

    const creativeCount = similarCreatives.length;
    const showWidgets = (isLoadingSimilarCreatives || !!creativeCount) && !similarCreativesError;

    const chartMetricOptions = useMemo(
        () =>
            chartMetrics.map(metric => ({
                ...metric,
                name: metric.value,
                display_name: metric.label,
            })),
        [chartMetrics]
    );

    const specAnalysisDropdowns = useMemo(() => [{ options: chartMetricOptions }], [chartMetricOptions]);

    const scatterDropdowns = useMemo(
        () => [
            {
                options: chartMetricOptions,
                label: `${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.X_AXIS_LABEL}`,
            },
            {
                options: chartMetricOptions,
                label: `${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.Y_AXIS_LABEL}`,
            },
        ],
        [chartMetricOptions]
    );

    const tagAnalysisDropdowns = useMemo(
        () => [
            {
                options: chartMetricOptions,
                label: `${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.METRIC_LABEL}`,
            },
        ],
        [chartMetricOptions]
    );

    const creativesLabel = translate(`${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.CREATIVES_LABEL}`);

    const buildSpecTooltipFormatter = useMemo(() => {
        return selectedMetric => {
            const metricConfig = chartMetrics.find(metric => metric.value === selectedMetric?.value);
            return getBreakdownTooltipFormatter(
                specAnalysis.creativeCountByCategory || {},
                selectedMetric?.label || selectedMetric?.display_name,
                metricConfig?.display_format,
                creativesLabel
            );
        };
    }, [chartMetrics, specAnalysis.creativeCountByCategory, creativesLabel]);

    const buildScatterTooltipFormatter = useMemo(() => {
        return (xMetric, yMetric) => getScatterTooltipFormatter(xMetric, yMetric);
    }, []);

    const specHidingChartMessage = specAnalysis.allSameValue
        ? `${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.SAME_VALUE_MESSAGE}`
        : null;

    return (
        <div className={sharedCss.tabContainer}>
            <div className={css.headerSection}>
                <div className={sharedCss.tabTitle}>
                    <Translate id={SIMILAR_CREATIVES_TAB_TITLE} />
                </div>
                {showWidgets && (
                    <div className={sharedCss.tabSubtitle}>
                        <Translate
                            id={`${SIMILAR_CREATIVES_KEYS_PREFIX}.${
                                isLoadingSimilarCreatives
                                    ? SimilarCreativesKeys.LOADING_SUBTITLE
                                    : SimilarCreativesKeys.TAB_SUBTITLE
                            }`}
                            data={{ count: creativeCount }}
                        />
                    </div>
                )}
            </div>
            {showWidgets ? (
                <>
                    <div className={css.overviewSection}>
                        <div className={sharedCss.sectionTitle}>
                            <Translate id={`${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.OVERVIEW_TITLE}`} />
                        </div>
                        <div className={css.gridContainer}>
                            {isLoadingSimilarCreatives || overview.loading ? (
                                <Spinner show />
                            ) : (
                                <AgGridReact
                                    className={css.agGrid}
                                    modules={[AllEnterpriseModule]}
                                    columnDefs={overview.columnDefs}
                                    rowData={overview.data}
                                    defaultColDef={DEFAULT_COL_DEF}
                                    domLayout="normal"
                                    rowHeight={36}
                                    headerHeight={36}
                                    suppressContextMenu
                                    suppressDragLeaveHidesColumns
                                    getRowId={params => params.data[Dimensions.CREATIVE_IMAGE_HASH]}
                                />
                            )}
                        </div>
                    </div>
                    <CreativeChart
                        title={`${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.SCATTER_ANALYSIS_TITLE}`}
                        dropdowns={scatterDropdowns}
                        data={scatterAnalysis.data}
                        loading={isLoadingSimilarCreatives || scatterAnalysis.loading}
                        chartType={SCATTER_CHART}
                        buildTooltipFormatter={buildScatterTooltipFormatter}
                    />
                    <CreativeChart
                        title={`${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.TAG_ANALYSIS_TITLE}`}
                        dropdowns={tagAnalysisDropdowns}
                        data={tagAnalysis.data}
                        loading={isLoadingSimilarCreatives || tagAnalysis.loading}
                        chartType={COLUMN_CHART}
                        breakdownsOptions={aiDimensionOptions}
                        selectedBreakdown={tagAnalysis.selectedBreakdown}
                        onBreakdownChange={tagAnalysis.setSelectedBreakdown}
                        breakdownsInputType={BreakdownType.DROPDOWN}
                        breakdownsDropdownLabel={`${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.AI_DIMENSION_LABEL}`}
                    />
                    <CreativeChart
                        title={`${SIMILAR_CREATIVES_KEYS_PREFIX}.${SimilarCreativesKeys.SPEC_ANALYSIS_TITLE}`}
                        dropdowns={specAnalysisDropdowns}
                        data={specAnalysis.allSameValue ? [] : specAnalysis.data}
                        loading={isLoadingSimilarCreatives || specAnalysis.loading}
                        chartType={BAR_CHART}
                        breakdownsOptions={specDimensions}
                        selectedBreakdown={specAnalysis.selectedBreakdown}
                        onBreakdownChange={specAnalysis.setSelectedBreakdown}
                        buildTooltipFormatter={buildSpecTooltipFormatter}
                        hidingChartMessage={specHidingChartMessage}
                    />
                </>
            ) : (
                <Translate
                    id={`${SIMILAR_CREATIVES_KEYS_PREFIX}.${
                        similarCreativesError
                            ? SimilarCreativesKeys.ERROR_MESSAGE
                            : SimilarCreativesKeys.NO_DATA_MESSAGE
                    }`}
                />
            )}
        </div>
    );
};

SimilarCreativesTab.propTypes = {
    similarCreatives: PropTypes.arrayOf(PropTypes.shape(CreativeType)),
    isLoadingSimilarCreatives: PropTypes.bool,
    similarCreativesError: PropTypes.bool,
    chartData: PropTypes.shape({
        overview: PropTypes.shape({
            data: PropTypes.array,
            columnDefs: PropTypes.array,
            loading: PropTypes.bool,
        }),
        specAnalysis: PropTypes.shape({
            data: PropTypes.array,
            loading: PropTypes.bool,
            selectedBreakdown: PropTypes.object,
            setSelectedBreakdown: PropTypes.func,
            creativeCountByCategory: PropTypes.object,
            allSameValue: PropTypes.bool,
        }),
        scatterAnalysis: PropTypes.shape({
            data: PropTypes.array,
            loading: PropTypes.bool,
        }),
        tagAnalysis: PropTypes.shape({
            data: PropTypes.array,
            loading: PropTypes.bool,
            selectedBreakdown: PropTypes.object,
            setSelectedBreakdown: PropTypes.func,
        }),
        aiDimensionOptions: PropTypes.array,
        chartMetrics: PropTypes.array,
        specDimensions: PropTypes.array,
    }),
    translate: PropTypes.func,
};

export default withLocalize(SimilarCreativesTab);
